import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default mini-infobar
      e.preventDefault();
      
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show the custom install banner
      setShowBanner(true);
    };

    const handleAppInstalled = () => {
      // Hide the banner when app is installed
      setShowBanner(false);
      setDeferredPrompt(null);
      console.log('[PWA] App installed successfully');
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Listen for the appinstalled event
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`[PWA] User response: ${outcome}`);

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    
    // Hide the banner
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    
    // Store dismissal in localStorage to avoid showing again this session
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if user dismissed recently (within 7 days)
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      
      if (Date.now() - dismissedTime < sevenDaysInMs) {
        setShowBanner(false);
      }
    }
  }, []);

  if (!showBanner || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md animate-in slide-in-from-bottom-5 duration-500">
      <Card className="relative overflow-hidden border-primary/20 bg-card/95 backdrop-blur-xl shadow-glass-elevated">
        {/* Glow effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 pointer-events-none" />
        
        <div className="relative p-4 space-y-3">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
          >
            <X className="h-4 w-4" />
          </Button>

          {/* Icon and header */}
          <div className="flex items-start gap-3 pr-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <Smartphone className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-foreground text-lg leading-tight">
                Install Crystal HACCP
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Get quick access from your home screen. Works offline and loads instantly.
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground pl-15">
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              Offline access
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              Fast loading
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              Home screen icon
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow"
            >
              <Download className="mr-2 h-4 w-4" />
              Install App
            </Button>
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="border-border/50 hover:bg-accent/50"
            >
              Not Now
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
